/**
 * level.ts — Pure geometry helpers for the city layout (Phaser-free, unit-tested).
 *
 * Used to keep hazards from spawning inside buildings. Rects are {x, y, w, h}
 * with x/y at the top-left corner.
 */

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** The centre of a rect, as `[x, y]`. Config rects are cornered; meshes are centred. */
export function rectCenter(r: Rect): [number, number] {
  return [r.x + r.w / 2, r.y + r.h / 2];
}

/** Whether a point is inside a rect, optionally grown by `margin` on every side. */
export function pointInRect(x: number, y: number, r: Rect, margin = 0): boolean {
  return (
    x >= r.x - margin &&
    x <= r.x + r.w + margin &&
    y >= r.y - margin &&
    y <= r.y + r.h + margin
  );
}

/** Whether a point falls inside any of the rects (with an optional shared margin). */
export function pointInAnyRect(x: number, y: number, rects: readonly Rect[], margin = 0): boolean {
  return rects.some((r) => pointInRect(x, y, r, margin));
}

/**
 * Whether two rects share any area. Touching edges do **not** count as an
 * overlap — a building whose back wall lands exactly on the footway edge is
 * flush with it, not standing on it, and the town is laid out that way on
 * purpose.
 */
export function rectsOverlap(a: Rect, b: Rect): boolean {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}

/** A copy of `r` grown by `margin` on every side (negative shrinks it). */
export function inflateRect(r: Rect, margin: number): Rect {
  return { x: r.x - margin, y: r.y - margin, w: r.w + margin * 2, h: r.h + margin * 2 };
}

/**
 * The shortest distance between two rects — 0 if they touch or overlap.
 *
 * The measure to reach for whenever the question is "how far apart are these
 * two things", because centre-to-centre is the wrong answer for anything with
 * size: a 200-wide green whose middle is 250 from the front door still has a
 * corner in the front garden. A point is a zero-sized rect, so this answers
 * point-to-rect too.
 */
export function rectGap(a: Rect, b: Rect): number {
  const dx = Math.max(0, a.x - (b.x + b.w), b.x - (a.x + a.w));
  const dy = Math.max(0, a.y - (b.y + b.h), b.y - (a.y + a.h));
  return Math.hypot(dx, dy);
}

/** A zero-sized rect at a point, for asking `rectGap` about one. */
export function pointRect(x: number, y: number): Rect {
  return { x, y, w: 0, h: 0 };
}

/** Whether `inner` lies entirely within `outer`. */
export function rectContains(outer: Rect, inner: Rect): boolean {
  return (
    inner.x >= outer.x &&
    inner.y >= outer.y &&
    inner.x + inner.w <= outer.x + outer.w &&
    inner.y + inner.h <= outer.y + outer.h
  );
}

/** Which face of a building the front door goes on. */
export type Facing = 'north' | 'south' | 'east' | 'west';

/**
 * A road running across the map: 'h' at y = pos, 'v' at x = pos.
 *
 * `from`/`to` bound it along its own axis (absent = the whole map) and `kind`
 * separates a carriageway from a cobbled pedestrian lane. Both are optional so
 * the authored town's `ROADS.SEGMENTS` still satisfies this shape; the canonical
 * definitions, and the resolvers that apply the defaults, live in
 * `systems/roads.ts`.
 */
export interface RoadSegment {
  orient: string;
  pos: number;
  from?: number;
  to?: number;
  kind?: 'street' | 'lane';
}

/**
 * Which way a building fronts — the face nearest a **pavement**, because that's
 * where its front door belongs. A door on the back of a terrace facing a blank
 * yard reads as a mistake even if you can't say why.
 *
 * ## Why footways rather than roads
 *
 * This used to measure from each face to the nearest road *centreline*, which
 * treats a road as an infinite line. Once a road can stop partway across town
 * that is simply wrong: a plot sitting past the end of a cul-de-sac would take
 * its front from a street it cannot see, and put its door on a blank flank.
 *
 * Measuring to the footway fixes that and does something better besides. A band
 * only counts for a face if it actually **lies in front of it** — north of a
 * north face, and overlapping it across the other axis — so a plot beside a gap
 * in the paving is not fronted onto the gap. That is the property the whole
 * "every entrance opens onto the pavement" rule rests on.
 *
 * Ties go to **south**, which is the face this camera can see: a south front
 * gets a patio and a visible door, a north one is drawn under its own roof (see
 * the camera-tilt rules in AGENTS.md).
 *
 * Pass the bands from `footwayBands()`, hoisted out of your loop — they are the
 * same for every building in a level.
 */
export function frontFacing(b: Rect, footways: readonly Rect[]): Facing {
  const gaps = faceGaps(b, footways);

  // South first, so it wins a tie: the only face this camera really shows.
  let best: Facing = 'south';
  for (const facing of ['north', 'east', 'west'] as const) {
    if (gaps[facing] < gaps[best]) best = facing;
  }
  // No pavement in front of any face — the caller has bigger problems, but
  // 'south' at least keeps the door on the face the camera can see.
  return gaps[best] === Infinity ? 'south' : best;
}

/**
 * How far it is from each face of a rect to the nearest pavement **in front of
 * that face**, or `Infinity` where there is none.
 *
 * A band only counts if it lies beyond the face and overlaps it across the other
 * axis — so the strip of paving running past the end of a terrace does not count
 * as being in front of its gable. Shared by `frontFacing` (which face is the
 * front?) and by the patio rule in `levelgen/town.ts` (does the front win by
 * enough that pulling the wall back can't swing the door round the side?).
 */
export function faceGaps(b: Rect, footways: readonly Rect[]): Record<Facing, number> {
  const spans = (lo: number, hi: number, from: number, to: number): boolean =>
    from < hi && to > lo;
  const alongX = (f: Rect): boolean => spans(b.x, b.x + b.w, f.x, f.x + f.w);
  const alongY = (f: Rect): boolean => spans(b.y, b.y + b.h, f.y, f.y + f.h);

  const out: Record<Facing, number> = {
    north: Infinity,
    south: Infinity,
    east: Infinity,
    west: Infinity,
  };

  for (const f of footways) {
    // A negative gap means the band is behind the face rather than in front.
    if (alongX(f)) {
      keep('south', f.y - (b.y + b.h));
      keep('north', b.y - (f.y + f.h));
    }
    if (alongY(f)) {
      keep('east', f.x - (b.x + b.w));
      keep('west', b.x - (f.x + f.w));
    }
  }

  function keep(facing: Facing, gap: number): void {
    if (gap >= 0 && gap < out[facing]) out[facing] = gap;
  }
  return out;
}

/**
 * How many window columns fit across one face of a building.
 *
 * Kept separate from the *gap* between them on purpose. The obvious formula —
 * `floor(usable / spacing)` — couples the two: widening the gap silently deletes
 * columns, so a 120-wide wall drops from two windows to one and looks bare.
 *
 * Rounding instead of flooring picks the nearer sensible count (a face 1.9
 * spacings wide wants two windows, not one), and the columns are then laid out
 * at exactly `spacing` apart and centred, so the gap stays what you asked for.
 * The final guard shrinks the count if the row genuinely wouldn't fit.
 */
export function facadeColumns(
  faceWidth: number,
  spacing: number,
  margin: number,
  windowWidth: number,
): number {
  const usable = faceWidth - margin * 2;
  if (usable <= 0 || spacing <= 0) return 1;

  let cols = Math.max(1, Math.round(usable / spacing));
  // Row width is (cols-1) gaps plus the outer half-windows; pull a column if
  // that would overhang the wall.
  while (cols > 1 && (cols - 1) * spacing + windowWidth > faceWidth - 8) cols--;
  return cols;
}

/**
 * The ground a front patio covers, given the face its door is on.
 *
 * Shared rather than computed twice: `buildings.ts` needs it to lay the paving,
 * and `yards.ts` needs it to *not* grow a garden there. Left duplicated, the
 * two drifted the obvious way — weeds coming up through somebody's front path.
 */
export function patioRect(b: Rect, front: Facing, depth: number, inset = 0): Rect {
  const alongX = front === 'north' || front === 'south';
  const width = (alongX ? b.w : b.h) - inset * 2;
  const w = alongX ? width : depth;
  const h = alongX ? depth : width;

  const cx = b.x + b.w / 2;
  const cy = b.y + b.h / 2;
  const out = (alongX ? b.h : b.w) / 2 + depth / 2;
  const px = cx + (front === 'east' ? out : front === 'west' ? -out : 0);
  const py = cy + (front === 'south' ? out : front === 'north' ? -out : 0);

  return { x: px - w / 2, y: py - h / 2, w, h };
}
